import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { chatAPI } from 'services/api';
import { RootState } from 'store/rootReducer';
import sort from 'utils/sort';

import { CanalInterface } from './canais';
import { ContatoInterface } from './contatos';

export declare interface GrupoInterface {
  id: string;
  foto: string;
  nome: string;
  status: GrupoStatusType;
  tipoCanal: string;
  totalMensagensNaoLidas: number;
  grupoChatCanais: CanalInterface[];
  ultimaMensagemEm: Date | string;
  canalMencionado?: boolean;
}

export type GrupoStatusType = 'CRIADO' | 'ATIVO' | 'INATIVO';

export declare interface CanalPreferenciaInterface {
  id: string;
  tenantId: string;
  grupoId: string;
  usuarioId: string;
  canalId: string;
  canalNome: string;
  canalCodigo: string;
  canalTipo: string;
  canalContato: string;
  canalAtivo: boolean;
}

type GruposState = {
  grupoSelecionado: GrupoInterface | undefined;
  grupos: GrupoInterface[];
  filteredGrupos: GrupoInterface[];
  loadingGrupos: boolean;
  canalSelecionadoId: string | undefined;
  loadingCanalPreferencia: boolean;
  contatosGrupo: ContatoInterface[];
  contatosGrupoCursor: CursorInterface;
  loadingContatosGrupo: boolean;
  loadingGrupoSelecionado: boolean;
  loadingMarcarGrupoComoLido: boolean;
  loadingMarcarGrupoComoNaoLido: boolean;
  loadingMarcarTodosGruposComoLidos: boolean;
};

// async thunks

export const setChannelPreference = createAsyncThunk(
  'grupos/setChannelPreference',
  async (params: { grupoId: string; canalId: string }) => {
    const { grupoId, canalId } = params;
    const response = await chatAPI.put(
      `v2/grupos-chat/${grupoId}/channel-preference`,
      { canalId },
    );
    return { canalId, data: response.data as CanalPreferenciaInterface };
  },
);

export const fetchGrupos = createAsyncThunk('grupos/fetchGrupos', async () => {
  const response = await chatAPI.get('grupos-chat');
  return response.data.data as GrupoInterface[];
});

export const fetchGrupoById = createAsyncThunk(
  'grupos/fetchGrupoById',
  async (grupoChatId: string) => {
    const response = await chatAPI.get(`grupos-chat/${grupoChatId}`);

    return response.data as GrupoInterface;
  },
);

export const fetchContatosByGrupo = createAsyncThunk(
  'grupos/fetchContatosGrupo',
  async (params: {
    grupoChatId: string;
    cursor?: CursorInterface;
    filter?: string;
  }) => {
    const { grupoChatId, cursor, filter } = params;
    const response = await chatAPI.get(`grupos-chat/${grupoChatId}/contatos`, {
      params: { afterCursor: cursor?.afterCursor, filter },
    });
    return {
      data: response.data.data as ContatoInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchContatosParaMencao = createAsyncThunk(
  'grupos/fetchContatosParaMencao',
  async (params: { grupoChatId: string; limit?: number; filter?: string }) => {
    const { grupoChatId, limit, filter } = params;
    const response = await chatAPI.get(`grupos-chat/${grupoChatId}/contatos`, {
      params: { limit, filter },
    });
    return response.data.data as ContatoInterface[];
  },
);

export const apagarMensagemGrupo = createAsyncThunk(
  'grupos/apagarMensagemGrupo',
  async (messageId: string) => {
    await chatAPI.delete(`grupos-chat/messages/${messageId}`);
  },
);

export const editarMensagemGrupo = createAsyncThunk(
  'grupos/editarMensagemGrupo',
  async (params: {
    messageId: string;
    newText: string;
    contatosMencionados?: import('types').ContatoMencionado[];
  }) => {
    const { messageId, newText, contatosMencionados } = params;
    const payload: Record<string, unknown> = { newText };
    if (contatosMencionados && contatosMencionados.length > 0) {
      payload.contatosMencionados = contatosMencionados;
    }
    const response = await chatAPI.put(
      `grupos-chat/messages/${messageId}`,
      payload,
    );

    return {
      messageId,
      newText,
      data: response.data,
    };
  },
);

export const fetchMarcarGrupoComoNaoLido = createAsyncThunk(
  'grupos/fetchMarcarGrupoComoNaoLido',
  async (grupoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `v2/grupos-chat/${grupoId}/mark-as-unread`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchMarcarGrupoComoLido = createAsyncThunk(
  'grupos/fetchMarcarGrupoComoLido',
  async (grupoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `v2/grupos-chat/${grupoId}/mark-as-read`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);
export const fetchMarcarTodosGruposComoLidos = createAsyncThunk(
  'grupos/fetchMarcarTodosGruposComoLidos',
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put('v2/grupos-chat/mark-as-read');
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initialState = {
  grupoSelecionado: undefined,

  grupos: [],
  filteredGrupos: [],
  loadingGrupos: false,

  contatosGrupo: [],
  contatosGrupoCursor: { afterCursor: '', beforeCursor: '' },
  loadingContatosGrupo: false,
  canalSelecionadoId: undefined,
  loadingGrupoSelecionado: false,
  loadingCanalPreferencia: false,
  loadingMarcarGrupoComoLido: false,
  loadingMarcarGrupoComoNaoLido: false,
  loadingMarcarTodosGruposComoLidos: false,
} as GruposState;

export const gruposSlice = createSlice({
  name: 'grupos',
  initialState,
  reducers: {
    setGrupoSelecionado(
      state,
      action: PayloadAction<GrupoInterface | undefined>,
    ) {
      state.grupoSelecionado = action.payload;
    },
    setTotalMensagensNaoLidasGrupo(
      state,
      action: PayloadAction<{
        grupoChatId: string;
        total: number;
      }>,
    ) {
      const { grupoChatId, total } = action.payload;
      const indexGrupo = state.grupos.findIndex(({ id }) => id === grupoChatId);
      const indexFilteredGrupo = state.filteredGrupos.findIndex(
        ({ id }) => id === grupoChatId,
      );

      const updateGroup = (grupo: GrupoInterface) => {
        grupo.totalMensagensNaoLidas = total;
        if (total === 0) {
          grupo.canalMencionado = false;
        }
      };

      // atualiza o valor das mensagens nao lidas do grupo na lista de grupos
      if (indexGrupo > -1) {
        updateGroup(state.grupos[indexGrupo]);
      }
      // atualiza o valor das mensagens nao lidas do grupo na lista de grupos filtrados
      if (indexFilteredGrupo > -1) {
        updateGroup(state.filteredGrupos[indexFilteredGrupo]);
      }
      // atualiza o total no grupo selecionado se ele for o mesmo do contador
      if (state.grupoSelecionado?.id === grupoChatId) {
        updateGroup(state.grupoSelecionado);
      }
    },
    setCanalMencionadoGrupo(
      state,
      action: PayloadAction<{ grupoChatId: string; canalMencionado: boolean }>,
    ) {
      const { grupoChatId, canalMencionado } = action.payload;
      const indexGrupo = state.grupos.findIndex(({ id }) => id === grupoChatId);
      const indexFilteredGrupo = state.filteredGrupos.findIndex(
        ({ id }) => id === grupoChatId,
      );

      if (indexGrupo > -1) {
        state.grupos[indexGrupo].canalMencionado = canalMencionado;
      }
      if (indexFilteredGrupo > -1) {
        state.filteredGrupos[indexFilteredGrupo].canalMencionado =
          canalMencionado;
      }
      if (state.grupoSelecionado?.id === grupoChatId) {
        state.grupoSelecionado.canalMencionado = canalMencionado;
      }
    },
    atualizarUltimaMensagemRecebidaGrupo(
      state,
      action: PayloadAction<{
        grupoChatId: string;
        ultimaMensagemEm: Date | string;
      }>,
    ) {
      const { grupoChatId, ultimaMensagemEm } = action.payload;
      const indexGrupo = state.grupos.findIndex(({ id }) => id === grupoChatId);
      const indexFilteredGrupo = state.filteredGrupos.findIndex(
        ({ id }) => id === grupoChatId,
      );

      if (indexGrupo > -1) {
        state.grupos[indexGrupo].ultimaMensagemEm = ultimaMensagemEm;
      }
      if (indexFilteredGrupo > -1) {
        state.filteredGrupos[indexFilteredGrupo].ultimaMensagemEm =
          ultimaMensagemEm;
      }
    },
    filterGrupos(state, action: PayloadAction<string>) {
      const value = action.payload.trimStart();
      const filteredValues = state.grupos.filter(grupo => {
        return grupo.nome.toLowerCase()?.includes(value.toLowerCase());
      });

      if (value.trim()) state.filteredGrupos = filteredValues;
      else state.filteredGrupos = state.grupos;
    },
    addNovoGrupo(state, action: PayloadAction<GrupoInterface>) {
      state.grupos.unshift(action.payload);
      state.filteredGrupos.unshift(action.payload);
    },
    atualizarFotoGrupo(
      state,
      action: PayloadAction<{ grupoChatId: string; foto: string }>,
    ) {
      const { grupoChatId, foto } = action.payload;
      const indexGrupo = state.grupos.findIndex(({ id }) => id === grupoChatId);
      const indexFilteredGrupo = state.filteredGrupos.findIndex(
        ({ id }) => id === grupoChatId,
      );

      // atualiza a foto do grupo se ele tiver na lista de grupos
      if (indexGrupo > -1) {
        state.grupos[indexGrupo].foto = foto;
      }
      // atualiza a foto do grupo se ele tiver na lista de grupos filtrados
      if (indexFilteredGrupo > -1) {
        state.filteredGrupos[indexFilteredGrupo].foto = foto;
      }
      // atualiza a foto do grupo selecionado, caso a foto atualizada seja a dele
      if (state.grupoSelecionado?.id === grupoChatId) {
        state.grupoSelecionado.foto = foto;
      }
    },
    atualizarStatusGrupo(
      state,
      action: PayloadAction<{ grupoChatId: string; status: GrupoStatusType }>,
    ) {
      const { grupoChatId, status } = action.payload;
      const indexGrupo = state.grupos.findIndex(({ id }) => id === grupoChatId);
      const indexFilteredGrupo = state.filteredGrupos.findIndex(
        ({ id }) => id === grupoChatId,
      );

      // atualiza o status do grupo se ele tiver na lista de grupos
      if (indexGrupo > -1) {
        state.grupos[indexGrupo].status = status;
      }
      // atualiza o status do grupo se ele tiver na lista de grupos filtrados
      if (indexFilteredGrupo > -1) {
        state.filteredGrupos[indexFilteredGrupo].status = status;
      }
      // atualiza o status do grupo selecionado, se o status dele foi o que mudou
      if (state.grupoSelecionado?.id === grupoChatId) {
        state.grupoSelecionado.status = status;
      }
    },
    reorderGrupos(state) {
      const cloneGrupos = Object.assign([], state.grupos);
      const cloneFilteredGrupos = Object.assign([], state.filteredGrupos);

      cloneGrupos.sort(sort('ultimaMensagemEm'));
      cloneFilteredGrupos.sort(sort('ultimaMensagemEm'));

      state.grupos = cloneGrupos;
      state.filteredGrupos = cloneFilteredGrupos;
    },
    resetGrupos(state) {
      state.grupos = initialState.grupos;
      state.loadingGrupos = initialState.loadingGrupos;
    },
    resetContatosGrupo(state) {
      state.contatosGrupo = initialState.contatosGrupo;
      state.contatosGrupoCursor = initialState.contatosGrupoCursor;
      state.loadingContatosGrupo = initialState.loadingContatosGrupo;
    },
    setCanalSelecionadoId(state, action: PayloadAction<string | undefined>) {
      state.canalSelecionadoId = action.payload;
    },
    setMarcarTodosGruposComoLidos(state) {
      state.grupos.forEach(grupo => {
        grupo.totalMensagensNaoLidas = 0;
        grupo.canalMencionado = false;
      });
      state.filteredGrupos.forEach(grupo => {
        grupo.totalMensagensNaoLidas = 0;
        grupo.canalMencionado = false;
      });
      if (state.grupoSelecionado) {
        state.grupoSelecionado.totalMensagensNaoLidas = 0;
        state.grupoSelecionado.canalMencionado = false;
      }
    },
    setMarcarGrupoComoLido(state, action: PayloadAction<string>) {
      const grupoId = action.payload;
      const updateTotal = (grupo: GrupoInterface) => {
        if (grupo.id === grupoId) {
          grupo.totalMensagensNaoLidas = 0;
          grupo.canalMencionado = false;
        }
      };
      state.grupos.forEach(updateTotal);
      state.filteredGrupos.forEach(updateTotal);
      if (state.grupoSelecionado?.id === grupoId) {
        state.grupoSelecionado.totalMensagensNaoLidas = 0;
        state.grupoSelecionado.canalMencionado = false;
      }
    },
    setMarcarGrupoComoNaoLido(state, action: PayloadAction<string>) {
      const grupoId = action.payload;
      const updateTotal = (grupo: GrupoInterface) => {
        if (grupo.id === grupoId) {
          grupo.totalMensagensNaoLidas = -1;
          grupo.canalMencionado = false;
        }
      };
      state.grupos.forEach(updateTotal);
      state.filteredGrupos.forEach(updateTotal);
      if (state.grupoSelecionado?.id === grupoId) {
        state.grupoSelecionado.totalMensagensNaoLidas = -1;
        state.grupoSelecionado.canalMencionado = false;
      }
    },
  },
  extraReducers(builder) {
    // Fetch grupos
    builder.addCase(fetchGrupos.pending, state => {
      state.loadingGrupos = true;
    });
    builder.addCase(fetchGrupos.fulfilled, (state, action) => {
      state.grupos = action.payload;
      state.filteredGrupos = action.payload;
      state.loadingGrupos = false;
    });
    // Fetch contatos grupos
    builder.addCase(fetchContatosByGrupo.pending, state => {
      state.loadingContatosGrupo = true;
    });
    builder.addCase(fetchContatosByGrupo.fulfilled, (state, action) => {
      const isFirstPage = !action.meta.arg.cursor?.afterCursor;
      if (isFirstPage) {
        state.contatosGrupo = action.payload.data;
      } else {
        state.contatosGrupo = state.contatosGrupo.concat(action.payload.data);
      }
      state.contatosGrupoCursor = action.payload.cursor;
      state.loadingContatosGrupo = false;
    });
    builder.addCase(fetchContatosByGrupo.rejected, state => {
      state.loadingContatosGrupo = false;
    });

    // Fetch grupo by id
    builder.addCase(fetchGrupoById.pending, state => {
      state.grupoSelecionado = undefined;
      state.canalSelecionadoId = undefined;
      state.loadingGrupoSelecionado = true;
    });
    builder.addCase(fetchGrupoById.fulfilled, (state, action) => {
      state.loadingGrupoSelecionado = false;

      state.grupoSelecionado = action.payload;

      const canaisAtivos = action.payload.grupoChatCanais.filter(
        canal => canal.isAtivo,
      );

      const canalPreferencial = canaisAtivos.find(
        canal => canal.isCanalPreferencial,
      );

      if (canalPreferencial) {
        state.canalSelecionadoId = canalPreferencial.id;
      } else if (canaisAtivos.length === 1) {
        state.canalSelecionadoId = canaisAtivos[0].id;
      } else {
        state.canalSelecionadoId = undefined;
      }
    });
    builder.addCase(fetchGrupoById.rejected, state => {
      state.loadingGrupoSelecionado = false;
    });

    // Set channel preference
    builder.addCase(setChannelPreference.pending, state => {
      state.loadingCanalPreferencia = true;
    });
    builder.addCase(setChannelPreference.fulfilled, (state, action) => {
      const { canalId } = action.payload;

      state.canalSelecionadoId = canalId;
      state.loadingCanalPreferencia = false;

      // Atualiza isCanalPreferencial nos canais do grupo selecionado
      if (state.grupoSelecionado?.grupoChatCanais) {
        state.grupoSelecionado.grupoChatCanais =
          state.grupoSelecionado.grupoChatCanais.map(canal => ({
            ...canal,
            isCanalPreferencial: canal.id === canalId,
          }));
      }
    });
    builder.addCase(setChannelPreference.rejected, state => {
      state.loadingCanalPreferencia = false;
    });

    // Mark grupo as read
    builder.addCase(fetchMarcarGrupoComoLido.pending, state => {
      state.loadingMarcarGrupoComoLido = true;
    });
    builder.addCase(fetchMarcarGrupoComoLido.fulfilled, state => {
      state.loadingMarcarGrupoComoLido = false;
    });
    builder.addCase(fetchMarcarGrupoComoLido.rejected, state => {
      state.loadingMarcarGrupoComoLido = false;
    });

    // Mark grupo as unread
    builder.addCase(fetchMarcarGrupoComoNaoLido.pending, state => {
      state.loadingMarcarGrupoComoNaoLido = true;
    });
    builder.addCase(fetchMarcarGrupoComoNaoLido.fulfilled, state => {
      state.loadingMarcarGrupoComoNaoLido = false;
    });
    builder.addCase(fetchMarcarGrupoComoNaoLido.rejected, state => {
      state.loadingMarcarGrupoComoNaoLido = false;
    });
    // Mark all grupos as read
    builder.addCase(fetchMarcarTodosGruposComoLidos.pending, state => {
      state.loadingMarcarTodosGruposComoLidos = true;
    });
    builder.addCase(fetchMarcarTodosGruposComoLidos.fulfilled, state => {
      state.loadingMarcarTodosGruposComoLidos = false;
    });
    builder.addCase(fetchMarcarTodosGruposComoLidos.rejected, state => {
      state.loadingMarcarTodosGruposComoLidos = false;
    });
  },
});

// actions

export const {
  setGrupoSelecionado,
  setTotalMensagensNaoLidasGrupo,
  setCanalMencionadoGrupo,
  atualizarUltimaMensagemRecebidaGrupo,
  filterGrupos,
  addNovoGrupo,
  atualizarFotoGrupo,
  atualizarStatusGrupo,
  reorderGrupos,
  resetContatosGrupo,
  resetGrupos,
  setCanalSelecionadoId,
  setMarcarGrupoComoLido,
  setMarcarGrupoComoNaoLido,
  setMarcarTodosGruposComoLidos,
} = gruposSlice.actions;

// selectors

export const gruposSelector = (state: RootState) => state.grupos;

export default gruposSlice.reducer;
